import React, { useState, type ImgHTMLAttributes } from 'react';
import { resolveMediaPath } from '@/shared/libs/resolve-media-path';
import { getGameJpegImagePath } from '@/shared/libs/get-game-jpeg-image-path';
import { ImageWrapper, Placeholder, StyledImage } from './styles';

// desktop чуть больше реального файла (168px) — иначе на 100% браузер перескакивает на mobile.
const GAME_IMAGE_WIDTH = {
  admin: 84,
  desktop: 170,
  mobile: 228,
  large: 528,
};

type GameImageFolder = keyof typeof GAME_IMAGE_WIDTH;

interface LazyGameIconProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'sizes'> {
  src?: string;
  game?: {
    providerId?: string;
    code?: string;
    imgVer?: number | string;
    variedImg?: boolean;
  };
  imgList?: string[];
  sizes?: string;
  priority?: boolean;
}

const LazyGameIcon = ({
  src,
  game,
  imgList,
  alt,
  className,
  sizes,
  priority,
  ...props
}: LazyGameIconProps) => {
  const providerId = game?.providerId;
  const code = game?.code;
  const imgVer = game?.imgVer;
  const variedImg = game?.variedImg;
  const imageKey = `${src}|${providerId}|${code}|${imgVer}|${variedImg}`;
  const [failedKey, setFailedKey] = useState<string | null>(null);

  if (failedKey === imageKey || (!variedImg && !src)) {
    return (
      <ImageWrapper className={className}>
        <Placeholder />
      </ImageWrapper>
    );
  }

  const image = (
    <StyledImage
      src={resolveMediaPath(
        variedImg ? getGameJpegImagePath(game) : (src ?? '')
      )}
      alt={alt}
      {...props}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      onError={() => setFailedKey(imageKey)}
      ref={(img) => {
        if (img?.complete && img.naturalWidth === 0) setFailedKey(imageKey);
      }}
    />
  );

  if (!variedImg) {
    return <ImageWrapper className={className}>{image}</ImageWrapper>;
  }

  const sources: string[] = [];
  for (const folder of imgList ?? []) {
    const width = GAME_IMAGE_WIDTH[folder as GameImageFolder];
    if (width) {
      sources.push(
        `${resolveMediaPath(`/assets/game/${folder}/${providerId}/${code}.webp?ver=${imgVer}`)} ${width}w`
      );
    }
  }

  return (
    <ImageWrapper className={className}>
      <picture>
        <source type="image/webp" srcSet={sources.join(', ')} sizes={sizes} />
        {image}
      </picture>
    </ImageWrapper>
  );
};

export { LazyGameIcon };
